import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateIsDiscardModalOpen,
  updateIsExplodeModalOpen,
  updateIsNextObjectiveModalOpen,
  updateObjectiveToCancel,
  updateObjectiveToExplode,
  updateObjectiveToRemove,
} from 'store/objective/reducer';
import { Store } from 'components/common/types/Store.types';
import warningSvg from 'assets/icons/warning.svg';
import Button, { Color, TextOverflow } from 'lib/Button';
import Modal from 'lib/Modal';
import { CodeNameModel } from 'components/common/types';
import {
  DirectSalesLineObjective,
  DirectSalesObjectiveFilterType,
} from 'components/common/types/DirectSalesCampaign.types';
import { createObjectivesMap, objectiveExists } from './utils';

type ObjectiveModalsProps = {
  objectives: DirectSalesLineObjective[];
  confirmModal: (objective: CodeNameModel, callbackFunction: (objective: CodeNameModel | null) => void) => void;
  confirmDiscardChanges: VoidFunction;
  confirmObjectiveChange: VoidFunction;
  confirmExplodeObjective: (
    objective: DirectSalesLineObjective,
    objectiveFilterType: DirectSalesObjectiveFilterType,
  ) => void;
};

const ObjectiveModals: React.FC<ObjectiveModalsProps> = ({
  objectives,
  confirmModal,
  confirmObjectiveChange,
  confirmDiscardChanges,
  confirmExplodeObjective,
}: ObjectiveModalsProps) => {
  const dispatch = useDispatch();
  const {
    settings: {
      isDiscardModalOpen,
      isNextObjectiveModalOpen,
      isExplodeModalOpen,
      objectiveToRemove,
      objectiveToCancel,
      objectiveToExplode,
    },
  } = useSelector((state: Store) => state.objective);

  const objectivesMap = useMemo(() => {
    return createObjectivesMap(objectives);
  }, [objectives]);

  const handleCancelExplodeObjective = (): void => {
    dispatch(updateIsExplodeModalOpen(false));
    dispatch(updateObjectiveToExplode({ objective: null, objectiveFilterType: null }));
  };

  return (
    <>
      {objectiveToCancel ? (
        <Modal
          dataTestId="objective-cancel-modal"
          isOpen={!!objectiveToCancel}
          onClose={() => dispatch(updateObjectiveToCancel(null))}
          icon={warningSvg}
          text="An objective cannot be created without at least a target and one filter selected, this objective will be removed."
          title={`Remove ${objectiveToCancel.name}?`}
          actionButtons={
            <>
              <Button label="Cancel" color={Color.SECONDARY} onClick={() => dispatch(updateObjectiveToCancel(null))} />
              <Button
                label="Remove objective"
                color={Color.DANGER}
                textOverflow={TextOverflow.VISIBLE}
                onClick={() =>
                  confirmModal(objectiveToCancel, (objective) => dispatch(updateObjectiveToCancel(objective)))
                }
              />
            </>
          }
        />
      ) : null}
      {objectiveToRemove ? (
        <Modal
          dataTestId="objective-removal-modal"
          isOpen={!!objectiveToRemove}
          onClose={() => dispatch(updateObjectiveToRemove(null))}
          icon={warningSvg}
          text="This action will permanently delete this objective."
          title={`Delete ${objectiveToRemove.name}?`}
          actionButtons={
            <>
              <Button label="Cancel" color={Color.SECONDARY} onClick={() => dispatch(updateObjectiveToRemove(null))} />
              <Button
                label="Delete objective"
                color={Color.DANGER}
                textOverflow={TextOverflow.VISIBLE}
                onClick={() =>
                  confirmModal(objectiveToRemove, (objective) => dispatch(updateObjectiveToRemove(objective)))
                }
              />
            </>
          }
        />
      ) : null}
      {isDiscardModalOpen ? (
        <Modal
          dataTestId="objective-discard-changes-modal"
          isOpen={isDiscardModalOpen}
          onClose={() => dispatch(updateIsDiscardModalOpen(false))}
          icon={warningSvg}
          text="Closing without clicking save is going to make you lose your changes."
          title="Discard changes?"
          actionButtons={
            <>
              <Button
                label="Cancel"
                color={Color.SECONDARY}
                onClick={() => dispatch(updateIsDiscardModalOpen(false))}
              />
              <Button
                label="Discard changes"
                color={Color.DANGER}
                textOverflow={TextOverflow.VISIBLE}
                onClick={confirmDiscardChanges}
              />
            </>
          }
        />
      ) : null}
      {isNextObjectiveModalOpen ? (
        <Modal
          dataTestId="objective-next-objective-modal"
          isOpen={isNextObjectiveModalOpen}
          onClose={() => dispatch(updateIsNextObjectiveModalOpen(false))}
          icon={warningSvg}
          text="Changing objective without clicking save is going to make you lose your changes."
          title="Change objective?"
          actionButtons={
            <>
              <Button
                label="Cancel"
                color={Color.SECONDARY}
                onClick={() => dispatch(updateIsNextObjectiveModalOpen(false))}
              />
              <Button
                label="Change objective"
                color={Color.DANGER}
                textOverflow={TextOverflow.VISIBLE}
                onClick={confirmObjectiveChange}
              />
            </>
          }
        />
      ) : null}
      {isExplodeModalOpen &&
      objectiveToExplode.objective &&
      objectiveToExplode.objectiveFilterType &&
      objectiveExists(objectivesMap, objectiveToExplode.objective.objectiveGroupId) ? (
        <Modal
          dataTestId="objective-explode-confirmation-modal"
          isOpen
          onClose={handleCancelExplodeObjective}
          icon={warningSvg}
          text={`This action will create ${
            objectiveToExplode.objectiveFilterType === 'frameList'
              ? objectiveToExplode.objective[objectiveToExplode.objectiveFilterType].listFiles.length +
                objectiveToExplode.objective[objectiveToExplode.objectiveFilterType].routeFrameCodes.length
              : objectiveToExplode.objective[objectiveToExplode.objectiveFilterType].length
          } objectives. Do you want to proceed?`}
          title="Explode objective"
          actionButtons={
            <>
              <Button label="Cancel" color={Color.SECONDARY} onClick={handleCancelExplodeObjective} />
              <Button
                label="Confirm"
                color={Color.PRIMARY}
                textOverflow={TextOverflow.VISIBLE}
                onClick={() => {
                  confirmExplodeObjective(
                    objectiveToExplode.objective as DirectSalesLineObjective,
                    objectiveToExplode.objectiveFilterType as DirectSalesObjectiveFilterType,
                  );
                }}
              />
            </>
          }
        />
      ) : null}
    </>
  );
};

export default ObjectiveModals;
